import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import type { RenderFileDto, RenderJobDto } from '@app/shared';
import { ThumbsDown, ThumbsUp } from '@phosphor-icons/react';
import { HelpHint } from '../components/HelpHint';
import { StepAction, StepLayout } from '../components/shell/StepLayout';
import { Button, Card, Chip, ProgressBar, Skeleton, SkeletonGroup, Spinner, Textarea, Toast } from '../components/ui';
import { useT } from '../i18n';
import { cn } from '../lib/cn';
import { renderKindLine } from '../lib/labels';
import { stepBack } from '../lib/layout';
import { formatBytes } from '../lib/render-format';
import { renderApi } from '../lib/render-client';
import { renderBlock, shouldPoll } from '../lib/render-view';
import { downloadFile } from '../lib/tma';

const linkButton =
  'flex items-center justify-center w-full rounded-full font-semibold transition-transform active:scale-[0.97]';
const secondaryLink = cn(linkButton, 'h-10 text-sm bg-surface-2 text-fg');
const primaryLink = cn(linkButton, 'h-12 text-base bg-accent text-accent-on');

/** The reel's video, or the slides of a carousel in a row. */
function Media({ job }: { job: RenderJobDto }) {
  const shown = job.files.filter((f) => f.kind !== 'archive');
  if (job.format === 'reel') {
    const video = shown.find((f) => f.kind === 'video');
    if (!video) return null;
    return (
      <video
        controls
        playsInline
        preload="metadata"
        poster={job.coverUrl ?? undefined}
        src={video.url}
        className="w-full aspect-[9/16] rounded-xl bg-black"
      />
    );
  }
  return (
    <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory -mx-4 px-4 pb-1">
      {shown.map((f) =>
        f.kind === 'video' ? (
          <video
            key={f.name}
            autoPlay
            muted
            loop
            playsInline
            src={f.url}
            className="w-64 flex-shrink-0 snap-center rounded-xl bg-black"
          />
        ) : (
          <img key={f.name} src={f.url} alt="" className="w-64 flex-shrink-0 snap-center rounded-xl" />
        ),
      )}
    </div>
  );
}

/** The file to save: the reel, or the zip of a carousel. */
function mainFile(job: RenderJobDto): RenderFileDto | null {
  const kind = job.format === 'reel' ? 'video' : 'archive';
  return job.files.find((f) => f.kind === kind) ?? null;
}

function Rating({ job }: { job: RenderJobDto }) {
  const t = useT();
  const qc = useQueryClient();
  const [asking, setAsking] = useState(false);
  const [comment, setComment] = useState('');
  const [thanks, setThanks] = useState<string | null>(null);
  const rate = useMutation({
    mutationFn: (v: { value: 'up' | 'down'; comment?: string }) => renderApi.rate(job.id, v.value, v.comment),
    onSuccess: (dto, v) => {
      qc.setQueryData(['render', job.id], dto);
      setAsking(v.value === 'down' && !v.comment);
      setThanks(v.comment ? t.render.commentSent : v.value === 'up' ? t.render.rated : null);
      if (v.comment) setComment('');
    },
    onError: () => setThanks(t.common.failed),
  });
  const value = job.rating?.value ?? null;

  return (
    <Card variant="surface" padding="md" className="space-y-3">
      <p className="text-sm font-medium">{t.render.rateQuestion}</p>
      <div className="flex gap-2">
        {(['up', 'down'] as const).map((v) => (
          <Chip
            key={v}
            selected={value === v}
            disabled={rate.isPending}
            onClick={() => rate.mutate({ value: v })}
            className="flex-1 justify-center"
          >
            {v === 'up' ? <ThumbsUp size={16} /> : <ThumbsDown size={16} />}
            {v === 'up' ? t.render.rateUp : t.render.rateDown}
          </Chip>
        ))}
      </div>
      {asking && (
        <div className="space-y-2">
          <p className="text-sm">{t.render.whatsWrong}</p>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t.render.whatsWrongPlaceholder}
            rows={3}
            maxLength={1000}
          />
          <Button
            variant="ink"
            fullWidth
            disabled={!comment.trim()}
            loading={rate.isPending}
            onClick={() => rate.mutate({ value: 'down', comment: comment.trim() })}
          >
            {t.render.send}
          </Button>
        </div>
      )}
      {thanks && <p className="text-xs text-hint">{thanks}</p>}
    </Card>
  );
}

/**
 * The build and its result (spec 6.2 «Сборка», «Результат»): the queue or the
 * stages with a bar while it works — the screen may be closed, the chat gets the
 * result; then the video or the slides, saving, a rebuild and the rating; or what
 * went wrong, the credits back and where to fix it.
 */
export function RenderPage() {
  const t = useT();
  const id = Number(useParams().id);
  const { pathname } = useLocation();
  const valid = Number.isInteger(id) && id > 0;
  const query = useQuery({
    queryKey: ['render', id],
    queryFn: () => renderApi.getRender(id),
    enabled: valid,
    refetchInterval: (q) => (shouldPoll(q.state.data) ? 3000 : false),
  });

  if (!valid) return <Navigate to="/" replace />;
  const job = query.data;
  const block = job ? renderBlock(job, t) : null;
  const title =
    block?.kind === 'done' ? t.render.resultTitle : block?.kind === 'failed' ? t.render.failedTitle : t.render.buildTitle;
  const file = job ? mainFile(job) : null;

  return (
    <StepLayout title={title} back={stepBack(pathname, { draftId: job?.draftId })} carousel={job?.format === 'carousel'}>
      <div className="space-y-4">
      {job && <p className="text-xs text-hint">{renderKindLine(job, t)}</p>}

      {query.isLoading && (
        <SkeletonGroup label={t.common.loading}>
          <Skeleton className="h-28" />
        </SkeletonGroup>
      )}
      {query.isError && <p className="text-sm text-hint">{t.render.notFound}</p>}

      {job && block?.kind === 'waiting' && (
        <Card variant="surface" padding="md" className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Spinner size="sm" />
            {block.text}
          </div>
          <p className="text-xs text-hint">{t.render.canClose}</p>
        </Card>
      )}

      {job && block?.kind === 'queued' && (
        <Card variant="surface" padding="md" className="space-y-2">
          <p className="text-sm font-medium">{block.text}</p>
          <p className="text-xs text-hint">{t.render.canClose}</p>
        </Card>
      )}

      {job && block?.kind === 'running' && (
        <Card variant="surface" padding="md" className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Spinner size="sm" />
            {block.words}
          </div>
          <ProgressBar value={block.percent} ariaLabel={block.words} />
          <p className="text-xs text-hint">{t.render.canClose}</p>
        </Card>
      )}

      {job && block?.kind === 'done' && (
        <>
          <Media job={job} />
          {file && (
            <StepAction>
              <Button variant="primary" size="lg" fullWidth onClick={() => downloadFile(file.downloadUrl, file.name)}>
                {`${t.render.download} · ${formatBytes(file.sizeBytes)}`}
              </Button>
            </StepAction>
          )}
          <div className="space-y-2">
            <Link to={`/drafts/${job.draftId}`} className={secondaryLink}>
              {t.render.editAndRebuild}
            </Link>
            <Link to={`/drafts/${job.draftId}`} className={secondaryLink}>
              {t.render.otherStyle}
            </Link>
          </div>
          <p className="flex items-center justify-center gap-1.5 text-xs text-hint text-center">
            {t.render.freeLeft(job.freeRebuildsLeft)}
            <HelpHint topic="rebuild" />
          </p>
          <Rating job={job} />
        </>
      )}

      {job && block?.kind === 'failed' && (
        <Card variant="surface" padding="md" className="space-y-3">
          <p className="text-sm whitespace-pre-line">{block.text}</p>
          {block.engineMessage && <p className="text-xs text-hint">{block.engineMessage}</p>}
          {block.refunded && <Toast tone="success">{block.refunded}</Toast>}
          <StepAction>
            <Link to={block.action.to} className={primaryLink}>
              {block.action.label}
            </Link>
          </StepAction>
        </Card>
      )}
      </div>
    </StepLayout>
  );
}
